/**
 * The apply loop (PIVOT-110, PIVOT-111), over the backend's `apply` router:
 * CV tailoring and the cover letter.
 *
 * `tailorCv`/`writeCoverLetter` persist nothing — each hands back a draft for
 * the candidate to review and edit. Only `saveArtifact` writes a document,
 * and only with exactly the content the candidate submitted (the
 * human-review gate made structural, constitution §I/§V). The two generation
 * calls share one daily budget on the backend (`apply_generations_per_user_
 * per_day`) — there is no per-kind split to mirror here.
 */

import { httpClient } from './client'
import type { ApplyArtifact, ArtifactKind, Locale } from '../types/domain'

/** `POST /api/apply/cv`'s response — no id, because nothing was saved. */
export interface TailorCvResponse {
  draft: string
  language: Locale
}

/**
 * Drafts, then polishes, a tailored CV from the candidate's stored CV parse.
 * 403 without current consent, 404 for an unknown job, 429 once the day's
 * shared generation budget is spent, 400 with no saved CV on file.
 */
export function tailorCv(jobId: string, signal?: AbortSignal): Promise<TailorCvResponse> {
  return httpClient.post<TailorCvResponse>('/apply/cv', { job_id: jobId }, signal)
}

/** `POST /api/apply/letter`'s response — same shape as `TailorCvResponse`,
 * kept as its own type (rather than a shared alias) because the two
 * responses answer different requests and PIVOT-110/PIVOT-111 evolve on
 * separate tickets. */
export interface WriteLetterResponse {
  draft: string
  language: Locale
}

/**
 * Drafts, then polishes, a cover letter from the candidate's stored CV parse
 * (PIVOT-111). Same error contract as `tailorCv`: 403/404/429/400 for the
 * same reasons, because both draw on the same consent/budget/saved-CV gates.
 */
export function writeCoverLetter(
  jobId: string,
  signal?: AbortSignal,
): Promise<WriteLetterResponse> {
  return httpClient.post<WriteLetterResponse>('/apply/letter', { job_id: jobId }, signal)
}

export interface SaveArtifactRequest {
  job_id: string
  kind: ArtifactKind
  content: string
  language: Locale
}

/** `POST /api/apply/artifacts` — persists exactly what the candidate submits. */
export function saveArtifact(
  body: SaveArtifactRequest,
  signal?: AbortSignal,
): Promise<ApplyArtifact> {
  return httpClient.post<ApplyArtifact>('/apply/artifacts', body, signal)
}

/** `GET /api/apply/artifacts` — this user's saved documents, newest first. */
export function getArtifacts(signal?: AbortSignal): Promise<ApplyArtifact[]> {
  return httpClient.get<ApplyArtifact[]>('/apply/artifacts', signal)
}

/**
 * `GET /api/apply/artifacts/{id}/download` — the saved content, byte-for-byte.
 * The response is `text/markdown`, not JSON, so this goes through
 * `httpClient.getText` rather than the JSON-parsing `get`.
 */
export function downloadArtifact(id: string, signal?: AbortSignal): Promise<string> {
  return httpClient.getText(`/apply/artifacts/${id}/download`, signal)
}
